import {
  L,
  TFarmLocation,
  TFarmLocationExt,
  TMinMax,
  TProviderAddressShort,
  TVesselLocation
} from 'types';
import { TVesselLocationExt, TVesselTrackPoint } from './vessel';

export type TMapAreas = {
  base: EMapArea.OSM | EMapArea.SEABED_BW | EMapArea.SEABED_COLOUR;
  feature: EMapArea.DEPTH_CURVES_COLOUR | EMapArea.EEZ_AREAS;
  zones: Array<EProtectedZone>;
  top: [
    EMapArea.DEPTH_CURVES_BW?,
    EMapArea.EEZ_LINES?,
    EMapArea.EEZ_LINES_ALT?,
    EMapArea.ICES?
  ];
};

export enum EMapAreaAction {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  TOGGLE = 'TOGGLE'
}

export enum EProtectedZone {
  SAC = 'SAC',
  SPA = 'SPA',
  MCZ = 'MCZ',
  NO_FJORD_LINES = 'NO_FJORD_LINES',
  NO_PROTECTED_TCA = 'NO_PROTECTED_TCA',
  NO_PROTECTED_CCR = 'NO_PROTECTED_CCR',
  NO_PROTECTED_CCNG = 'NO_PROTECTED_CCNG',
  NO_PROTECTED_PZS = 'NO_PROTECTED_PZS',
  NO_SEISMIC_ONGOING = 'NO_SEISMIC_ONGOING',
  NO_SEISMIC_PLANNED = 'NO_SEISMIC_PLANNED',
  SCOTTISH_MPA = 'SCOTTISH_MPA'
}

export enum EMapArea {
  DEPTH_CURVES_BW = 'DEPTH_CURVES_BW',
  DEPTH_CURVES_COLOUR = 'DEPTH_CURVES_COLOUR',
  EEZ_AREAS = 'EEZ_AREAS',
  EEZ_LINES = 'EEZ_LINES',
  EEZ_LINES_ALT = 'EEZ_LINES_ALT',
  ICES = 'ICES',
  OSM = 'OSM',
  SEABED_BW = 'SEABED_BW',
  SEABED_COLOUR = 'SEABED_COLOUR'
}

export enum EMapLayer {
  CHLOROPHYLL = 'CHLOROPHYLL',
  CURR = 'currents',
  GUSTS = 'gust',
  ICE = 'ICE',
  ICE_COVERAGE = 'ICE_COVERAGE',
  PHYTOPLANKTON = 'PHYTOPLANKTON',
  SALINITY = 'SALINITY',
  SEATEMP = 'SEATEMP',
  SURFACETEMP = 'SURFACETEMP',
  TEMP = 'temp',
  WAVES = 'waves',
  WIND = 'wind',
  UPWELLING = 'UPWELLING',
  ZOOPLANKTON = 'ZOOPLANKTON',
  // to be removed later
  CONTOUR_BW = 'CONTOUR_BW',
  EEZ = 'EEZ',
  ICES = 'ICES',
  SEABED = 'SEABED'
}

export enum EMapIceLayer {
  ANTARCTIC_1 = 'ANTARCTIC_1',
  ANTARCTIC_2 = 'ANTARCTIC_2',
  ANTARCTIC_3 = 'ANTARCTIC_3',
  BARENTS_SEA = 'BARENTS_SEA',
  EAST_GREENLAND = 'EAST_GREENLAND',
  GREENLAND = 'GREENLAND',
  SVALBARD = 'SVALBARD',
  WEST_GREENLAND = 'WEST_GREENLAND'
}

export enum EMapView {
  ATLANTIC = 'ATLANTIC',
  PACIFIC = 'PACIFIC'
}

export type TTrackPoint = {
  vesselId: number;
  point: TVesselTrackPoint;
  latlng: L.LatLng;
};

export enum EMarkerCategory {
  AREA = 'areas',
  CAGE = 'cages',
  LAYER_VALUE = 'layerValues',
  LOCATION = 'locations',
  SERVICE = 'services',
  VESSEL = 'vessels',
  ZONE_INFO = 'zoneInfo'
}

export type TLayerPointLocation = {
  coords: {
    x: number;
    y: number;
    z: number;
  };
  ij: {
    i: number;
    j: number;
  };
  latitude: number;
  longitude: number;
};

export type TLayerPointValue = {
  latitude: number;
  longitude: number;
  timeStamp: string;
  title: string;
  units: string;
  value: number;
};

export type TLocationCoordinates = {
  accuracy: number;
  heading: number;
  latitude: number;
  longitude: number;
  speed: number;
};

export type TPosition = [number, number];

export type TMapEntity =
  | TFarmLocation
  | TLayerPointValue
  | TProviderAddressShort
  | TVesselLocation;

export type TDraggableMapEntity = TFarmLocationExt | TVesselLocationExt;

export type TMapEntityState = {
  color?: string; // Track color. Vessels only.
  index?: number; // Position on the panel.
  panel: EMiniCardPanel; // If null, then the card isn't minimized.
  type: EMarkerCategory;
};

export enum EMiniCardPanel {
  BOTTOM = 'BOTTOM',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT'
}

export enum ETrackMode {
  DEFAULT = 'DEFAULT',
  FISHING_ACTIVITY = 'FISHING_ACTIVITY',
  MULTI_SPEED = 'MULTI_SPEED'
}

export type TLayerValues = {
  depth: number;
  depthRange: TMinMax;
  maxRange: [number, number];
  opacity: number;
  range: [number, number];
};

/**/
export type TLayerInfo = {
  dateList: TDateListItem[];
  elevationList: number[];
  name:
    | 'PlanktonData'
    | 'PlanktonSmooth'
    | 'SalinityData'
    | 'SalinitySmooth'
    | 'SeaTemperatureData'
    | 'SeaTemperatureSmooth'
    | 'ZooplanktonData'
    | 'ZooplanktonSmooth';
  workspace: 'cns';
};
/**/
export type TDateListItem = {
  disabled: boolean;
  dateTime: string; // 2024-07-16T23:56:15Z
  label: string; // Jul 17
};

/**/
export type TZoneInfo = {
  id: string;
  name: EProtectedZone;
  properties: {
    actual_start_date?: string;
    area_name?: string;
    description?: string;
    name?: string;
    paragraph?: string;
    planned_end_date?: string;
    planned_start_date?: string;
    speciestype_name?: string;
    survey_company?: string;
    survey_type?: string;
    type_name?: string;
  };
};

/**/
export type TZoneInfoGroup = {
  id: string;
  items: TZoneInfo[];
  lat: number;
  lng: number;
};
